import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Home Care",
  "Assisted Living",
  "Career Support (Re-Entry)",
  "Career Support (Special Needs)",
  "Sober Living",
  "Other Healthcare",
];

const REVENUE_RANGES = [
  "Under $250K",
  "$250K – $500K",
  "$500K – $1M",
  "$1M – $3M",
  "$3M – $7M",
  "$7M+",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  business_type: "",
  annual_revenue: "",
  location: "",
  description: "",
};

export default function SubmissionForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (form.phone.trim().length < 5) return "Please enter a valid phone.";
    if (!form.business_type) return "Please select a business type.";
    if (!form.annual_revenue) return "Please select an annual revenue range.";
    if (form.description.trim().length < 10)
      return "Please describe your business briefly (at least 10 characters).";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Submission received. We'll be in touch shortly.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="submit"
      data-testid="submission-section"
      className="py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="eyebrow">Start the conversation</span>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-5 text-[#1A2621] leading-[1.05]">
            Tell us a little about
            <span className="italic text-[#284A3B]"> your business.</span>
          </h2>
          <p className="mt-6 text-lg text-[#4A5D54] leading-relaxed">
            Submissions are confidential and reviewed by a partner — not a
            broker. We&rsquo;ll respond within 2 business days.
          </p>

          <ul className="mt-10 space-y-4 text-[#4A5D54]">
            {[
              "No financial documents required at this stage",
              "Reviewed by an actual acquisition partner",
              "Reply within 2 business days",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="mt-0.5 text-[#C86A51]" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="submission-success"
              className="bg-[#E6EAE3] rounded-2xl border border-[#D5DACF] p-10"
            >
              <div className="w-12 h-12 rounded-full bg-[#284A3B] text-[#FDFBF7] flex items-center justify-center">
                <CheckCircle2 size={22} />
              </div>
              <h3 className="font-serif-display text-3xl mt-6 text-[#1A2621]">
                Thank you — your submission is in.
              </h3>
              <p className="mt-4 text-[#4A5D54]">
                A partner will review the details and reach out within two
                business days. If your inquiry is time-sensitive, you can also
                email us at{" "}
                <a
                  href="mailto:info@heracq.com"
                  className="text-[#284A3B] underline underline-offset-4"
                >
                  info@heracq.com
                </a>
                .
              </p>
              <button
                data-testid="submit-another-btn"
                onClick={() => setSubmitted(false)}
                className="mt-8 inline-flex items-center gap-2 text-[#284A3B] font-medium hover:text-[#C86A51] transition-colors"
              >
                Submit another business <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form
              data-testid="submission-form"
              onSubmit={onSubmit}
              className="bg-white rounded-2xl border border-[#D5DACF] p-8 md:p-10 shadow-sm space-y-6"
              noValidate
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium text-[#4A5D54]"
                  >
                    Your name
                  </Label>
                  <Input
                    id="name"
                    data-testid="form-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-[#4A5D54]"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@yourcare.com"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="phone"
                    className="text-sm font-medium text-[#4A5D54]"
                  >
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 123-4567"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="location"
                    className="text-sm font-medium text-[#4A5D54]"
                  >
                    Business location <span className="text-[#9aa39c]">(optional)</span>
                  </Label>
                  <Input
                    id="location"
                    data-testid="form-location"
                    value={form.location}
                    onChange={update("location")}
                    placeholder="City, State"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-[#4A5D54]">
                    Business type
                  </Label>
                  <Select
                    value={form.business_type}
                    onValueChange={(v) => update("business_type")(v)}
                  >
                    <SelectTrigger
                      data-testid="form-business-type"
                      className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                    >
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((b) => (
                        <SelectItem
                          key={b}
                          value={b}
                          data-testid={`option-business-${b}`}
                        >
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#4A5D54]">
                    Annual revenue
                  </Label>
                  <Select
                    value={form.annual_revenue}
                    onValueChange={(v) => update("annual_revenue")(v)}
                  >
                    <SelectTrigger
                      data-testid="form-annual-revenue"
                      className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                    >
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent>
                      {REVENUE_RANGES.map((r) => (
                        <SelectItem
                          key={r}
                          value={r}
                          data-testid={`option-revenue-${r}`}
                        >
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label
                  htmlFor="description"
                  className="text-sm font-medium text-[#4A5D54]"
                >
                  Briefly describe your business
                </Label>
                <Textarea
                  id="description"
                  data-testid="form-description"
                  rows={5}
                  value={form.description}
                  onChange={update("description")}
                  placeholder="Years in operation, size, licenses, why you're considering selling..."
                  className="mt-2 bg-white border-[#D5DACF] rounded-xl"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C86A51] hover:bg-[#B35A42] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full px-10 py-4 font-medium transition-colors shadow-sm"
                >
                  {submitting ? "Sending..." : "Get My Free Valuation"}
                  {!submitting && <ArrowRight size={18} />}
                </button>

                <div
                  data-testid="valuation-badge"
                  className="inline-flex items-center gap-2 rounded-full bg-[#E6EAE3] border border-[#D5DACF] px-4 py-2 text-sm text-[#284A3B]"
                >
                  <span className="w-2 h-2 rounded-full bg-[#C86A51] animate-pulse" />
                  Free indicative valuation within 48 hours
                </div>
              </div>

              <p className="text-xs text-[#4A5D54]">
                By submitting you agree to be contacted regarding a potential
                acquisition. We treat all information as confidential.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
